import { Purchase, PurchaseItem } from "types";
import { getAuditChangeTime, getAuditChangeUserId } from "./auditGroupingHelper";
import { ChangeQuantityItem } from "./Items/ChangeQuantityItem";
import { getAuditData } from "@/models/Audit";

const QUANTITY_CHANGE_GROUPING_THRESHOLD_SECONDS = 10;

const isQuantityChangeAudit = (change: any, purchase: Purchase, item: PurchaseItem): boolean => {
    if (!change.data) return false;
    const audit = getAuditData(change);
    return ChangeQuantityItem.supportsAudit(purchase, item, audit as any);
};

const mergeQuantityChangeAudits = (base: any, next: any): any => {
    const baseItems: any[] = Array.isArray(base?.data?.items)
        ? base.data.items
        : Object.values(base?.data?.items ?? {});
    const nextItems: any[] = Array.isArray(next?.data?.items)
        ? next.data.items
        : Object.values(next?.data?.items ?? {});

    const itemsById: Record<string, any> = {};

    for (const item of baseItems) {
        itemsById[item.id] = { ...item };
    }

    for (const item of nextItems) {
        if (itemsById[item.id]) {
            const mergedLineItems: Record<string, any> = {};
            for (const li of Object.values(itemsById[item.id].lineItems ?? {})) {
                mergedLineItems[(li as any).id] = li;
            }
            for (const li of Object.values(item.lineItems ?? {})) {
                mergedLineItems[(li as any).id] = li;
            }
            itemsById[item.id] = { ...itemsById[item.id], lineItems: mergedLineItems };
        } else {
            itemsById[item.id] = { ...item };
        }
    }

    return { ...base, data: { ...base.data, items: itemsById } };
};

export const applyQuantityChangeGrouping = (sortedList: any[], purchase: Purchase, item?: PurchaseItem): any[] => {
    if (!item) {
        return sortedList;
    }

    // Collect positions of quantity-change audits in the sorted list
    const qtyPositions: number[] = [];
    for (const [index, element] of sortedList.entries()) {
        if (isQuantityChangeAudit(element, purchase, item)) {
            qtyPositions.push(index);
        }
    }

    if (qtyPositions.length <= 1) {
        return sortedList;
    }

    // Group consecutive qty-change audits that are within the time window and same user.
    // Non-qty items between them are ignored for the purpose of grouping decisions.
    const groups: Array<number[]> = [];
    let currentGroup: number[] = [qtyPositions[0]];

    for (let index = 1; index < qtyPositions.length; index++) {
        const previousAudit = sortedList[qtyPositions[index - 1]];
        const currentAudit = sortedList[qtyPositions[index]];

        const previousTime = getAuditChangeTime(previousAudit);
        const currentTime = getAuditChangeTime(currentAudit);
        const diffInSeconds = Math.abs(previousTime.diff(currentTime, "second"));
        const isSameUser = getAuditChangeUserId(previousAudit) === getAuditChangeUserId(currentAudit);
        const hasUser = !!getAuditChangeUserId(currentAudit);

        if (diffInSeconds <= QUANTITY_CHANGE_GROUPING_THRESHOLD_SECONDS && isSameUser && hasUser) {
            currentGroup.push(qtyPositions[index]);
        } else {
            groups.push(currentGroup);
            currentGroup = [qtyPositions[index]];
        }
    }
    groups.push(currentGroup);

    // Build a map: first position in group → merged audit; remaining positions → removed
    const mergedByPosition = new Map<number, any>();
    const removedPositions = new Set<number>();

    for (const group of groups) {
        if (group.length <= 1) continue;

        let merged = sortedList[group[0]];
        for (let index = 1; index < group.length; index++) {
            merged = mergeQuantityChangeAudits(merged, sortedList[group[index]]);
            removedPositions.add(group[index]);
        }
        mergedByPosition.set(group[0], merged);
    }

    // Rebuild list: replace first-in-group with merged, drop the rest
    return sortedList
        .map((change, index) => {
            if (removedPositions.has(index)) return null;
            if (mergedByPosition.has(index)) return mergedByPosition.get(index);
            return change;
        })
        .filter(Boolean);
};
